import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { LocalizedProcessingBadge, useT, type Translations } from '@/shared/i18n'
import { ToolHero } from '@/shared/ui/ToolHero'

type FaviconFitMode = 'contain' | 'cover'
type FaviconMime = 'image/jpeg' | 'image/png' | 'image/webp'
type FaviconCopy = Translations['faviconGenerator']
type FaviconErrorCode =
  | 'unsupported_type'
  | 'too_large'
  | 'too_large_image'
  | 'corrupt_image'
  | 'canvas_error'
  | 'zip_failed'

interface LoadedFaviconImage {
  file: File
  url: string
  width: number
  height: number
  mime: FaviconMime
}

interface FaviconAsset {
  name: string
  size: number
  blob: Blob
  url: string
}

interface FaviconPackage {
  assets: FaviconAsset[]
  manifestBlob: Blob
  zipBlob: Blob
}

const UTILITY_KEY = 'favicon-generator'
const MAX_FILE_SIZE = 50 * 1024 * 1024
const MAX_PIXELS = 36_000_000
const ACCEPTED_TYPES: FaviconMime[] = ['image/jpeg', 'image/png', 'image/webp']
const FAVICON_SIZES = [16, 32, 180, 192, 512] as const

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

function fileMime(file: File): FaviconMime | null {
  const mime = file.type.toLowerCase()
  if (ACCEPTED_TYPES.includes(mime as FaviconMime)) {
    return mime as FaviconMime
  }

  const name = file.name.toLowerCase()
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg'
  if (name.endsWith('.png')) return 'image/png'
  if (name.endsWith('.webp')) return 'image/webp'
  return null
}

function loadImage(src: string, copy: FaviconCopy): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error(copy.errorsText.corrupt))
    img.src = src
  })
}

function faviconName(size: number): string {
  if (size === 16) return 'favicon-16x16.png'
  if (size === 32) return 'favicon-32x32.png'
  if (size === 180) return 'apple-touch-icon.png'
  return `icon-${size}.png`
}

function canvasToPng(
  canvas: HTMLCanvasElement,
  copy: FaviconCopy,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error(copy.errorsText.canvas))
        return
      }
      resolve(blob)
    }, 'image/png')
  })
}

function drawFaviconCanvas(
  source: HTMLImageElement,
  size: number,
  fitMode: FaviconFitMode,
  copy: FaviconCopy,
): HTMLCanvasElement {
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size

  const context = canvas.getContext('2d')
  if (!context) {
    throw new Error(copy.errorsText.canvas)
  }

  const sourceWidth = source.naturalWidth
  const sourceHeight = source.naturalHeight
  const scale =
    fitMode === 'cover'
      ? Math.max(size / sourceWidth, size / sourceHeight)
      : Math.min(size / sourceWidth, size / sourceHeight)
  const width = sourceWidth * scale
  const height = sourceHeight * scale
  const x = (size - width) / 2
  const y = (size - height) / 2

  context.clearRect(0, 0, size, size)
  context.imageSmoothingEnabled = true
  context.imageSmoothingQuality = 'high'
  context.drawImage(source, x, y, width, height)

  return canvas
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

function buildManifest(): Blob {
  const manifest = {
    name: 'Website',
    short_name: 'Website',
    icons: [
      {
        src: '/icon-192.png',
        sizes: '192x192',
        type: 'image/png',
      },
      {
        src: '/icon-512.png',
        sizes: '512x512',
        type: 'image/png',
      },
    ],
    theme_color: '#ffffff',
    background_color: '#ffffff',
    display: 'standalone',
  }

  return new Blob([`${JSON.stringify(manifest, null, 2)}\n`], {
    type: 'application/manifest+json',
  })
}

function revokePackage(pack: FaviconPackage | null): void {
  pack?.assets.forEach((asset) => URL.revokeObjectURL(asset.url))
}

export function FaviconGeneratorPage() {
  const t = useT()
  const copy = t.faviconGenerator
  const [image, setImage] = useState<LoadedFaviconImage | null>(null)
  const [fitMode, setFitMode] = useState<FaviconFitMode>('contain')
  const [result, setResult] = useState<FaviconPackage | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [dropActive, setDropActive] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const resultRef = useRef<FaviconPackage | null>(null)

  useEffect(() => {
    resultRef.current = result
  }, [result])

  useEffect(() => {
    return () => {
      if (image) {
        URL.revokeObjectURL(image.url)
      }
    }
  }, [image])

  useEffect(() => () => revokePackage(resultRef.current), [])

  const clearResult = useCallback(() => {
    setResult((previous) => {
      revokePackage(previous)
      return null
    })
  }, [])

  const setLoadedImage = useCallback(
    async (file: File) => {
      setError(null)
      clearResult()

      const mime = fileMime(file)
      if (!mime) {
        setError(copy.errorsText.unsupported)
        toolError(UTILITY_KEY, 'unsupported_type')
        return
      }
      if (file.size > MAX_FILE_SIZE) {
        setError(copy.errorsText.tooLarge)
        toolError(UTILITY_KEY, 'too_large')
        return
      }

      const url = URL.createObjectURL(file)
      try {
        const decoded = await loadImage(url, copy)
        const width = decoded.naturalWidth
        const height = decoded.naturalHeight
        if (width < 1 || height < 1 || width * height > MAX_PIXELS) {
          throw new Error(copy.errorsText.tooLargeImage)
        }

        const nextImage: LoadedFaviconImage = { file, url, width, height, mime }
        setImage((previous) => {
          if (previous) {
            URL.revokeObjectURL(previous.url)
          }
          return nextImage
        })
      } catch (err) {
        URL.revokeObjectURL(url)
        const message = err instanceof Error ? err.message : copy.errorsText.corrupt
        const code: FaviconErrorCode =
          message === copy.errorsText.tooLargeImage ? 'too_large_image' : 'corrupt_image'
        setError(message)
        toolError(UTILITY_KEY, code)
      }
    },
    [clearResult, copy],
  )

  const addFiles = useCallback(
    (incoming: FileList | File[]) => {
      const [file] = Array.from(incoming)
      if (file) {
        void setLoadedImage(file)
      }
    },
    [setLoadedImage],
  )

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files.length > 0) {
        addFiles(event.target.files)
        event.target.value = ''
      }
    },
    [addFiles],
  )

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setDropActive(true)
  }, [])

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setDropActive(false)
  }, [])

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      event.stopPropagation()
      setDropActive(false)
      if (event.dataTransfer.files.length > 0) {
        addFiles(event.dataTransfer.files)
      }
    },
    [addFiles],
  )

  const handleFitModeChange = useCallback(
    (nextMode: FaviconFitMode) => {
      setFitMode(nextMode)
      setError(null)
      clearResult()
    },
    [clearResult],
  )

  const handleGenerate = useCallback(async () => {
    if (!image) return

    const startTime = performance.now()
    toolInvoke(UTILITY_KEY, {
      mode: fitMode,
      input_mime: image.mime,
      width: image.width,
      height: image.height,
    })
    setIsProcessing(true)
    setError(null)
    clearResult()

    let assets: FaviconAsset[] = []
    try {
      const decoded = await loadImage(image.url, copy)

      for (const size of FAVICON_SIZES) {
        const canvas = drawFaviconCanvas(decoded, size, fitMode, copy)
        const blob = await canvasToPng(canvas, copy)
        assets.push({
          name: faviconName(size),
          size,
          blob,
          url: URL.createObjectURL(blob),
        })
      }

      const manifestBlob = buildManifest()
      const { default: JSZip } = await import('jszip')
      const zip = new JSZip()
      assets.forEach((asset) => zip.file(asset.name, asset.blob))
      zip.file('site.webmanifest', manifestBlob)
      const zipBlob = await zip.generateAsync({ type: 'blob' })
      const nextPackage: FaviconPackage = { assets, manifestBlob, zipBlob }

      setResult(nextPackage)
      downloadBlob(zipBlob, 'favicon-package.zip')
      toolSuccess(UTILITY_KEY, Math.round(performance.now() - startTime), {
        mode: fitMode,
        input_mime: image.mime,
      })
    } catch (err) {
      assets.forEach((asset) => URL.revokeObjectURL(asset.url))
      const message = err instanceof Error ? err.message : copy.errorsText.packageFailed
      const code: FaviconErrorCode =
        message === copy.errorsText.canvas ? 'canvas_error' : 'zip_failed'
      setError(message)
      toolError(UTILITY_KEY, code, { mode: fitMode })
    } finally {
      setIsProcessing(false)
    }
  }, [clearResult, copy, fitMode, image])

  const handleDownloadPackage = useCallback(() => {
    if (result) {
      downloadBlob(result.zipBlob, 'favicon-package.zip')
    }
  }, [result])

  const selectedAsset = result?.assets.find((asset) => asset.size === 32) ?? null

  return (
    <div className="mx-auto max-w-6xl">
      <ToolHero
        eyebrow={copy.eyebrow}
        title={copy.title}
        description={copy.description}
        chips={['PNG, JPG, WebP', t.common.maxFileSize]}
        collapsed={Boolean(image)}
      />

      <div className={cn('grid grid-cols-1 gap-6', image && 'lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start')}>
        <div className="min-w-0 space-y-6">
          <Card
            className={cn(
              'mx-auto max-w-3xl transition-colors',
              image && 'max-w-none',
              dropActive && 'border-primary bg-primary/5',
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <CardContent className="p-4 text-center sm:p-8">
              <div className="mx-auto mb-5 hidden h-16 w-16 place-items-center sm:grid rounded-md border bg-background text-xs font-semibold text-muted-foreground">
                ICO
              </div>
              <p className="mb-2 hidden text-lg font-medium [@media(pointer:fine)]:block">{copy.dropTitle}</p>
              <p
                className={cn(
                  'mx-auto mb-5 max-w-md text-sm leading-6 text-muted-foreground',
                  image && 'hidden sm:block',
                )}
              >
                {copy.dropDescription}
              </p>
              <input
                ref={inputRef}
                type="file"
                accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={handleInputChange}
              />
              <Button disabled={isProcessing} onClick={() => inputRef.current?.click()}>
                {image ? copy.replaceImage : copy.chooseFile}
              </Button>
            </CardContent>
          </Card>

          {image && (
            <Card className="overflow-hidden">
              <CardContent className="space-y-5 p-4 sm:p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="truncate text-lg font-semibold tracking-normal" title={image.file.name}>
                      {image.file.name}
                    </h2>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <span className="rounded-md border bg-background px-2 py-1 text-xs font-medium text-muted-foreground">
                        {image.mime.replace('image/', '').toUpperCase()}
                      </span>
                      <span className="rounded-md border bg-background px-2 py-1 text-xs text-muted-foreground">
                        {formatSize(image.file.size)}
                      </span>
                      <span className="rounded-md border bg-background px-2 py-1 text-xs text-muted-foreground">
                        {image.width} x {image.height}
                      </span>
                    </div>
                  </div>
                  <Button variant="outline" onClick={() => inputRef.current?.click()}>
                    {copy.replaceImage}
                  </Button>
                </div>

                <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_260px]">
                  <div className="rounded-md border bg-background p-4">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <h2 className="text-base font-semibold tracking-normal">
                        {copy.previewTitle}
                      </h2>
                      <span className="rounded-md border bg-card px-2 py-1 text-xs text-muted-foreground">
                        {fitMode}
                      </span>
                    </div>
                    <div className="mx-auto grid aspect-square max-w-sm place-items-center rounded-md bg-muted p-4">
                      <div className="aspect-square w-full max-w-[280px] overflow-hidden rounded-md border bg-background">
                        <img
                          src={image.url}
                          alt={image.file.name}
                          className={cn(
                            'h-full w-full',
                            fitMode === 'cover' ? 'object-cover' : 'object-contain',
                          )}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="rounded-md border bg-background p-4">
                    <h2 className="text-base font-semibold tracking-normal">
                      {copy.outputPreviewTitle}
                    </h2>
                    <div className="mt-4 grid min-h-[180px] place-items-center rounded-md bg-muted p-4">
                      {selectedAsset ? (
                        <div className="grid gap-3 text-center">
                          <img
                            src={selectedAsset.url}
                            alt={selectedAsset.name}
                            className="mx-auto h-20 w-20 rounded-md border bg-background object-contain p-1"
                          />
                          <span className="text-sm font-medium">{selectedAsset.name}</span>
                          <span className="text-xs text-muted-foreground">
                            {formatSize(selectedAsset.blob.size)}
                          </span>
                        </div>
                      ) : (
                        <div className="grid h-20 w-20 place-items-center rounded-md border bg-background text-xs font-semibold text-muted-foreground">
                          32
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {result && (
                  <div className="grid gap-3 sm:grid-cols-5">
                    {result.assets.map((asset) => (
                      <div key={asset.name} className="rounded-md border bg-background p-3 text-center">
                        <img
                          src={asset.url}
                          alt={asset.name}
                          className="mx-auto h-12 w-12 rounded-md border bg-card object-contain p-1"
                        />
                        <div className="mt-2 text-sm font-medium">{asset.size} x {asset.size}</div>
                        <div className="mt-1 truncate text-xs text-muted-foreground" title={asset.name}>
                          {asset.name}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {error && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {error}
            </div>
          )}
        </div>

        {image && (
          <aside className="min-w-0 space-y-4 lg:sticky lg:top-24">
            <Card>
              <CardContent className="space-y-5 p-5">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-lg font-semibold tracking-normal">{copy.settings}</h2>
                  <LocalizedProcessingBadge variant="local" />
                </div>

                <div className="space-y-2">
                  <div className="text-sm font-medium">{copy.fitMode}</div>
                  <div className="grid grid-cols-2 overflow-hidden rounded-md border">
                    {copy.fitModes.map((item) => (
                      <button
                        key={item.key}
                        type="button"
                        className={cn(
                          'px-3 py-2 text-sm font-medium transition-colors',
                          item.key === 'cover' && 'border-l',
                          fitMode === item.key
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-background hover:bg-muted',
                        )}
                        onClick={() => handleFitModeChange(item.key as FaviconFitMode)}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>

                <Button
                  className="w-full"
                  size="lg"
                  onClick={handleGenerate}
                  disabled={isProcessing}
                >
                  {isProcessing ? copy.processing : copy.generateButton}
                </Button>

                {result && (
                  <Button className="w-full" variant="outline" onClick={handleDownloadPackage}>
                    {copy.downloadZip}
                  </Button>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-3 p-5">
                <h2 className="text-lg font-semibold tracking-normal">{copy.packageTitle}</h2>
                <div className="space-y-2 text-sm text-muted-foreground">
                  {copy.packageFiles.map((filename) => (
                    <div key={filename} className="flex justify-between gap-3">
                      <span className="truncate">{filename}</span>
                      <strong className="font-medium text-foreground">PNG</strong>
                    </div>
                  ))}
                  <div className="flex justify-between gap-3">
                    <span>site.webmanifest</span>
                    <strong className="font-medium text-foreground">JSON</strong>
                  </div>
                </div>
              </CardContent>
            </Card>

            <p className="rounded-md border bg-card px-3 py-2 text-xs leading-5 text-muted-foreground">
              {copy.privacyNote}
            </p>
          </aside>
        )}
      </div>

      <section className="mt-12 grid gap-4 border-t pt-8 lg:grid-cols-3">
        {copy.faq.map((item) => (
          <div key={item.question}>
            <h2 className="text-base font-semibold tracking-normal">{item.question}</h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              {item.answer}
            </p>
          </div>
        ))}
      </section>
    </div>
  )
}
